import React from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormActions,
    FormFields,
    FormMultiselectDropdown,
    FormTextField,
    mapToDropdownOption
} from "sprint-common-web";

import { DeliveryEndpointType } from "../../../../common/types/DeliveryEndpointType";
import { DeliveryEndpointDetails, SmartjogDeliveryEndpointDetails } from "../../listing/DeliveryEndpointItem";
import { useEditSmartjogDeliveryEndpointForm } from "./useEditSmartjogDeliveryEndpointForm";

export interface EditSmartjogDeliveryEndpointFormProps {
    onCancel(): void;
    onSubmit(): void;
    deliveryEndpointId: string;
}

export interface EditSmartjogDeliveryEndpointFormInputs {
    name: string;
    type: DeliveryEndpointType.Smartjog;
    distributionListIds: string[];
}

export const EditSmartjogDeliveryEndpointForm: React.VFC<EditSmartjogDeliveryEndpointFormProps> = ({
    deliveryEndpointId,
    onSubmit,
    onCancel
}: EditSmartjogDeliveryEndpointFormProps) => {
    const { deliveryEndpoint, distributionLists, editDeliveryEndpoint } = useEditSmartjogDeliveryEndpointForm(
        deliveryEndpointId
    );

    ensureSmartjogDetails(deliveryEndpoint.details);

    const { errors, control, formState, reset, handleSubmit } = useForm<EditSmartjogDeliveryEndpointFormInputs>({
        defaultValues: {
            name: deliveryEndpoint.name,
            distributionListIds: deliveryEndpoint.details.distributionLists.map((item) => String(item.id))
        },
        criteriaMode: "all"
    });

    const handleEditDeliveryEndpoint = async (inputs: EditSmartjogDeliveryEndpointFormInputs) => {
        await editDeliveryEndpoint(inputs);
        onSubmit();
    };

    const handleCancelButtonClick = () => {
        reset();
        onCancel();
    };

    return (
        <Form onSubmit={handleSubmit(handleEditDeliveryEndpoint)}>
            <FormFields>
                <FormTextField
                    label="Name"
                    control={control}
                    name="name"
                    errors={errors}
                    rules={{ required: "Enter name" }}
                />

                <FormMultiselectDropdown
                    label="Distribution lists"
                    name="distributionListIds"
                    options={distributionLists.map((item) => mapToDropdownOption(item))}
                    control={control}
                    errors={errors}
                    rules={{ required: "Select 1 or more distribution list" }}
                />
            </FormFields>
            <FormActions
                isLoading={formState.isSubmitting || formState.isValidating}
                onCancel={handleCancelButtonClick}
            />
        </Form>
    );
};

export const ensureSmartjogDetails: (
    value: DeliveryEndpointDetails
) => asserts value is SmartjogDeliveryEndpointDetails = (value) => {
    if (value.type !== DeliveryEndpointType.Smartjog) {
        throw Error("Delivery endpoint should be of type 'smartjog'");
    }
};
